/**
 * DepotPicker — top-nav switcher for the multi-depot rollout.
 *
 * Users with access to only one depot see a static chip (no dropdown).
 * Users with multiple depots (admins or anyone granted >1 depot) see a
 * dropdown that also offers "All my depots". The current pick drives
 * every subsequent API call via the `X-Depot` header (api.js).
 */
import { useRef, useState, useEffect } from "react";
import { ChevronDown, Globe2, MapPin } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const COUNTRY_FLAG = {
  "GH-TEM": "🇬🇭",
  "TG-LFW": "🇹🇬",
  "GA-LBV": "🇬🇦",
};

export default function DepotPicker({ className = "" }) {
  const { depots, depot, setDepot } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const accessible = (depots.depots || []).filter((d) => d.accessible);
  const canPickAll = depots.can_pick_all;

  if (accessible.length === 0) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-300 bg-amber-50 text-amber-900 text-[11px] font-mono ${className}`} data-testid="depot-picker-locked">
        <MapPin className="w-3 h-3" />
        No depot access
      </div>
    );
  }

  // Single-depot user — no dropdown, just a static chip.
  if (accessible.length === 1 && !canPickAll) {
    const only = accessible[0];
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#99D420]/40 bg-[#99D420]/10 text-[#1A1410] text-[11px] font-mono ${className}`} data-testid="depot-picker-static">
        <span className="text-sm leading-none">{COUNTRY_FLAG[only.code] || "📍"}</span>
        <span className="font-bold">{only.code}</span>
      </div>
    );
  }

  const current = depot && accessible.find((d) => d.code === depot);
  const label = current ? `${COUNTRY_FLAG[current.code] || "📍"} ${current.code}` : "🌐 All depots";

  return (
    <div ref={ref} className={`relative ${className}`} data-testid="depot-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[#99D420]/40 bg-white/95 hover:bg-white text-[#1A1410] text-[11px] font-mono shadow-sm"
        data-testid="depot-picker-button"
      >
        <span className="font-bold whitespace-nowrap">{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded shadow-xl z-50 overflow-hidden"
          data-testid="depot-picker-menu"
        >
          {canPickAll && (
            <button
              onClick={() => { setDepot(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-gray-50 ${!depot ? "bg-[#99D420]/10 font-bold" : ""}`}
              data-testid="depot-opt-ALL"
            >
              <Globe2 className="w-3.5 h-3.5 text-[#006E9E]" />
              All my depots
              <span className="ml-auto font-mono text-[10px] text-gray-400">{accessible.length}</span>
            </button>
          )}
          {canPickAll && <div className="border-t border-gray-100" />}
          {accessible.map((d) => (
            <button
              key={d.code}
              onClick={() => { setDepot(d.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-gray-50 ${depot === d.code ? "bg-[#99D420]/10 font-bold" : ""}`}
              data-testid={`depot-opt-${d.code}`}
            >
              <span className="text-base leading-none">{COUNTRY_FLAG[d.code] || "📍"}</span>
              <div className="min-w-0 flex-1">
                <div className="font-mono font-bold text-[11px]">{d.code}</div>
                <div className="text-[10px] text-gray-500 truncate">{d.name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
